/**
 * Centralized strings for internationalization
 * All user-facing text in Afrikaans (default language)
 */

export interface IStrings {
  // App Title and Navigation
  appTitle: string
  viewStatistics: string
  dismissNotification: string
  retry: string

  // Game Messages
  wordNotFound: string
  defaultWord: string
  
  // Daily Word Notifications
  newDailyWordAvailable: string
  errorLoadingNewWord: string
  refreshPageMessage: string
  
  // Word Bank Messages
  wordBankLoadSuccess: (count: number) => string
  wordBankLoadFailed: (attempt: number, maxAttempts: number) => string
  wordBankRetryFailed: string
  wordBankUsingBackup: (count: number) => string
  wordBankUsingEmbedded: (count: number) => string
  
  // Game Over Messages
  dailyWordelComplete: string
  congratulations: string
  solvedInAttempts: (attempts: number) => string
  betterLuckTomorrow: string
  youllGetNextOne: string
  todaysWord: string
  statistics: string
  comeBackTomorrow: string
  dailyChallengeInfo: string
  
  // Statistics
  overallPerformance: string
  played: string
  winRate: string
  currentStreak: string
  maxStreak: string
  guessDistribution: string
  averageGuesses: (avg: number) => string
  recentGames: string
  lastSevenDays: string
  noGamesPlayed: string
  completeFirstGame: string
  closeStatistics: string
  
  // Keyboard
  enter: string
  delete: string
  
  // Date Formatting
  dateFormat: {
    weekday: 'long' | 'short' | 'narrow'
    year: 'numeric' | '2-digit'
    month: 'long' | 'short' | 'narrow' | 'numeric' | '2-digit'
    day: 'numeric' | '2-digit'
  }
  shortDateFormat: {
    month: 'short' | 'long' | 'narrow' | 'numeric' | '2-digit'
    day: 'numeric' | '2-digit'
  }
  
  // Accessibility Labels
  a11y: {
    viewStatistics: string
    dismissNotification: string
    retryLoadingWordBank: string
    closeStatistics: string
  }
  
  // Error Messages (for console/debugging)
  errors: {
    initializingGame: string
    updatingGameState: string
    completingGame: string
    loadingStatistics: string
  }
}

// Default Afrikaans strings
export const strings: IStrings = {
  // App Title and Navigation
  appTitle: "Woordel 🌶️",
  viewStatistics: "📊",
  dismissNotification: "×",
  retry: "Probeer weer",

  // Game Messages
  wordNotFound: "Woord nie gevind nie",
  defaultWord: "WOORD",
  
  // Daily Word Notifications
  newDailyWordAvailable: "Nuwe daaglikse woord beskikbaar! Vandag se woord is gereed.",
  errorLoadingNewWord: "Fout met laai van nuwe daaglikse woord. Verfris asseblief die bladsy.",
  refreshPageMessage: "Verfris asseblief die bladsy.",
  
  // Word Bank Messages
  wordBankLoadSuccess: (count: number) => `Woordbank suksesvol gelaai met ${count} woorde!`,
  wordBankLoadFailed: (attempt: number, maxAttempts: number) => 
    `Kon nie woordbank laai nie (poging ${attempt}/${maxAttempts}). Probeer weer...`,
  wordBankRetryFailed: "Herprobeer het gefaal. Gaan jou internetverbinding na en probeer weer.",
  wordBankUsingBackup: (count: number) => 
    `Kon nie hoofwoordbank na 3 pogings laai nie. Gebruik rugsteunwoordlys met ${count} woorde.`,
  wordBankUsingEmbedded: (count: number) => 
    `Gebruik ingebedde woordlys met ${count} woorde (lêerlaai het gefaal)`,
  
  // Game Over Messages
  dailyWordelComplete: "Daaglikse Woordel Voltooi!",
  congratulations: "🎉 Geluk!",
  solvedInAttempts: (attempts: number) => 
    `Jy het vandag se Woordel in ${attempts} poging${attempts !== 1 ? 's' : ''} opgelos!`,
  betterLuckTomorrow: "Probeer môre weer!",
  youllGetNextOne: "Jy sal die volgende een kry!",
  todaysWord: "Vandag se woord:",
  statistics: "📊 Statistieke",
  comeBackTomorrow: "🕛 Kom môre terug vir 'n nuwe daaglikse uitdaging!",
  dailyChallengeInfo: "Elke dag bring 'n nuwe 5-letter Afrikaanse woord om te raai.",
  
  // Statistics
  overallPerformance: "Algehele Prestasie",
  played: "Gespeel",
  winRate: "Wen Koers",
  currentStreak: "Huidige Reeks",
  maxStreak: "Maks Reeks",
  guessDistribution: "Raai Verspreiding",
  averageGuesses: (avg: number) => `Gemiddelde raaiskote: ${avg}`,
  recentGames: "Onlangse Speletjies",
  lastSevenDays: "Laaste 7 Dae",
  noGamesPlayed: "Nog geen speletjies gespeel nie!",
  completeFirstGame: "Voltooi jou eerste daaglikse Woordel om statistieke te sien.",
  closeStatistics: "Statistieke",
  
  // Keyboard
  enter: "VOER IN",
  delete: "VEE UIT",
  
  // Date Formatting
  dateFormat: {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  },
  shortDateFormat: {
    month: 'short',
    day: 'numeric'
  },
  
  // Accessibility Labels
  a11y: {
    viewStatistics: "Statistieke",
    dismissNotification: "Maak kennisgewing toe",
    retryLoadingWordBank: "Probeer weer om woordbank te laai",
    closeStatistics: "Maak statistieke toe"
  },
  
  // Error Messages (for console/debugging)
  errors: {
    initializingGame: "Fout met initialiseer van spel:",
    updatingGameState: "Fout met opdatering van speltoestand:",
    completingGame: "Fout met voltooiing van spel:",
    loadingStatistics: "Fout met laai van statistieke:"
  }
}

// Export default strings for convenience
export default strings